import Button from 'components/buttons/Button/Button'
import CircleCheckIcon from 'components/icons/CircleCheckIcon'
import RocketIcon from 'components/icons/RocketIcon'
import type React from 'react'
import { useHistory } from 'react-router-dom'
import { trackUpsellClicked } from 'utils/eventPushes'
import ModalWindow from '../ModalWindow/ModalWindow'
import styles from './ProUpgradeModal.module.scss'

interface ProUpgradeModalProps {
  isOpened: boolean
  onCloseModal: () => void
  featureName: string
  // The gated feature's config key (ids, not the display title, go to analytics).
  featureKey: string
  benefitSentence?: string
  bullets?: string[]
  onUpgrade?: () => void
  isTrial?: boolean
}

const ProUpgradeModal: React.FC<ProUpgradeModalProps> = ({
  isOpened,
  onCloseModal,
  featureName,
  featureKey,
  benefitSentence,
  bullets = [],
  onUpgrade,
  isTrial = false,
}) => {
  const history = useHistory()

  const handleUpgradeClick = () => {
    trackUpsellClicked({ source: 'pro_upgrade_modal', featureName: featureKey })
    onUpgrade?.()
    history.push('/account/subscription')
    onCloseModal()
  }

  return (
    <ModalWindow
      isOpened={isOpened}
      isCloseButton
      onCloseModal={onCloseModal}
      closeButtonPlacement={{ top: '38px', right: '24px' }}
      className={styles.container}
    >
      <div className={styles.content} data-test="pro-upgrade-modal">
        <div className={styles.badgeRow}>
          <span className={styles.iconTile}>
            <RocketIcon width={22} height={22} color="currentColor" />
          </span>
          <span className={styles.eyebrow}>{isTrial ? 'Trial limit reached' : 'Pro feature'}</span>
        </div>

        <h2 className={styles.title}>{featureName}</h2>
        {benefitSentence && <p className={styles.benefit}>{benefitSentence}</p>}

        {bullets.length > 0 && (
          <div className={styles.unlocks}>
            <p className={styles.unlocksLabel}>Upgrade to Pro to unlock</p>
            <ul className={styles.bullets}>
              {bullets.map((bullet) => (
                <li key={bullet} className={styles.bullet}>
                  <span className={styles.check}>
                    <CircleCheckIcon width={18} height={18} strokeColor="currentColor" />
                  </span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.actions}>
          <Button width="auto" variant="outlined" onClick={onCloseModal} dataTest="pro-upgrade-dismiss">
            Not now
          </Button>
          <Button width="auto" onClick={handleUpgradeClick} dataTest="pro-upgrade-submit">
            Upgrade to Pro
          </Button>
        </div>
      </div>
    </ModalWindow>
  )
}

export default ProUpgradeModal
